import { EDiscountType } from './promoCodes'
import {
  EDeliveryCheckCategory,
  EDeliverySubsidyReason,
  EDeliveryWeightCategory,
  EDeliveryZone
} from './logisticsSettings'
import type { TClientPlatform } from './analytics'

// Самостоятельной доставки ещё нет в сгенерированных типах @mivis/petmart-api — домёрживаем
declare global {
  namespace Components {
    namespace Schemas {
      enum ERecieveMethod {
        OWN_DELIVERY = 'OWN_DELIVERY'
      }
    }
  }
}

export interface IDeliveryCostSharing {
  actual_cost: number
  buyer_share: number
  seller_share: number
  platform_share: number
  // Старые снимки расчёта могут содержать эти поля. Текущий backend их не возвращает.
  zone?: EDeliveryZone
  distance_km?: number
  distance_source?: string
  carrier: string
  policy_version: number
  reason: EDeliverySubsidyReason
  minimum_item_price_per_kg: number
  check_category?: EDeliveryCheckCategory
  weight_category?: EDeliveryWeightCategory
  rule_key?: string
}

export interface IAdminOrdersQueryRequest extends Omit<Components.Schemas.IAdminOrdersQueryRequest, 'status'> {
  status?: EOrderStatus
  // фильтр по промокоду — параметр добавлен на бэке, в пакете типов его ещё нет
  promo_code_id?: string
  // 'true' — бэк не режет супер-заказ между страницами и отдаёт multi_order_id
  super_orders?: string
  // заказы одного покупателя, в пакете типов параметра нет
  user_id?: string
}
export interface IOrdersQueryFilters extends Omit<IAdminOrdersQueryRequest, 'skip' | 'limit'> { }
export interface IAdminOrdersQueryResponse extends Components.Schemas.IAdminOrdersQueryResponse { }
export interface IOrderDeliveryMeta {   //У Дани нет доступа к апдейту пакета, потом убрать
  isFreeDeliveryToPvz?: boolean
  discount_pvz_delivery_sum?: number
  promo_details?: IOrderPromoDetails
  delivery_info?: Components.Schemas.IPopulatedOrder['delivery_info'] & {
    external_id?: string
    label?: string
    provider?: TPickupPointProvider
    error?: string
    tracking_url?: string
    cdek_uuid?: string
    cdek_error?: string
    provider_cost?: number
    // Сколько СДЭК взял за доставку до ПВЗ, когда принял посылку и обмерил её сам
    fact_cost?: number
    shipment_point?: IShipmentPoint
    cost_sharing?: IDeliveryCostSharing
  }
}
// Пункт, куда продавец сдаёт посылку: у СДЭКа code — код ПВЗ, у Яндекса — id станции.
// Пусто у заказов до 10.2026, у них пункт берётся из профиля продавца
export interface IShipmentPoint {
  provider: TPickupPointProvider
  code: string
  name?: string
  address?: string
}
// Стоимость у перевозчика (provider_cost) бэкенд отдаёт только супер-админу
export interface IShipmentPointQuoteResponse {
  provider: TPickupPointProvider
  current: {
    shipment_point: IShipmentPoint | null
    provider_cost?: number | null
  }
  next: {
    shipment_point: IShipmentPoint
    provider_cost?: number
  }
}
export interface IShipmentPointChangeResponse {
  provider: TPickupPointProvider
  shipment_point: IShipmentPoint
  provider_cost?: number
}
export interface ICdekRefreshResponse {
  statusChanged: boolean
  registrationError: string
}
export interface IOrderPromoDetails {
  is_applied: boolean
  promo_code_id?: string
  promo_code?: string
  discount_type?: 'PERCENT' | 'FIXED'
  discount_value?: number
  discount_charge_type?: 'STORE' | 'SELLER'
  original_sum?: number
  discounted_sum?: number
  discount_amount?: number
  seller_price_before_discount?: number
  seller_price_after_discount?: number
  seller_discount_impact?: number
  store_discount_impact?: number
}
export enum EOrderHistoryEvent {
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  // Денежные этапы продажи: бэкенд добавляет их в историю при чтении заказа
  PENDING_7_DAYS = 'PENDING_7_DAYS',
  READY_FOR_WITHDRAWAL = 'READY_FOR_WITHDRAWAL',
  WITHDRAWAL_REQUESTED = 'WITHDRAWAL_REQUESTED',
  PENDING_WITHDRAWAL = 'PENDING_WITHDRAWAL',
  WITHDRAWN = 'WITHDRAWN'
}
// Запись без status — событие, после которого статус заказа прежний (event)
export interface IOrderStatusHistoryItem {
  status?: EOrderStatus
  event?: EOrderHistoryEvent
  comment?: string
  changed_at: string
}
export interface IOrderTimeline {   //У Дани нет доступа к апдейту пакета, потом убрать
  status_history?: IOrderStatusHistoryItem[]
  order_confirmation_time?: string
  delivery_preparation_time?: string
  delivery_completion_time?: string
  paid_at?: string
  confirmation_deadline_extra_hours?: number
  shipping_deadline_extra_hours?: number
}
export interface IAdminExtendOrderDeadlineRequest {
  hours: number
}
export interface IAdminExtendOrderDeadlineResponse {
  confirmation_deadline_extra_hours: number
  shipping_deadline_extra_hours: number
}
export interface IOrderSuperOrder {   //У Дани нет доступа к апдейту пакета, потом убрать
  // приходит только у заказов супер-заказа, в котором больше одного заказа
  multi_order_id?: string | null
}
export interface IOrderSellerDeadlines {   //У Дани нет доступа к апдейту пакета, потом убрать
  skip_order_deadlines?: boolean
  order_confirmation_deadline_hours?: number | null
  order_shipping_deadline_hours?: number | null
}
export interface IOrderCancelPenalty {   //У Дани нет доступа к апдейту пакета, потом убрать
  commission_rate?: number | null
  commission_penalty_rate?: number
  // отмен у продавца за 30 дней на момент оформления заказа
  seller_cancel_count?: number
  // переплата продавца относительно комиссии по базовой ставке
  penalty_commission?: number
}
export type TSellerNotifyChannel = 'email' | 'sms' | 'push' | 'telegram' | 'max'
export interface IOrderSellerNotified {   //У Дани нет доступа к апдейту пакета, потом убрать
  // каналы, которые приняли уведомление продавцу о заказе; поля нет — уведомление не отправляли
  seller_notified_via?: TSellerNotifyChannel[]
}
export interface IOrderSellerCancelCount {   //У Дани нет доступа к апдейту пакета, потом убрать
  cancel_count?: number
}
export interface IOrderSellerDeliveryPoints {   //У Дани нет доступа к апдейту пакета, потом убрать
  // приходит только в карточке заказа
  yandex_dropoff_station_id?: string | null
  ozon_dropoff_point_id?: number | null
}
// Приходит в ответе на отмену заказа или товара, если отмену засчитали продавцу
export interface ISellerCancelPenalty {
  cancel_count: number
  commission_penalty: number
  previous_commission_penalty: number
  base_commission: number
  disabled: boolean
  disabled_until?: string | null
}
export type TPickupPointProvider = 'CDEK' | 'YANDEX' | 'OZON'
export type IOrderPickupPoint = NonNullable<Components.Schemas.IPopulatedOrder['pvz']> & {
  provider?: TPickupPointProvider
}
export interface ITableOrder extends Omit<Components.Schemas.IPopulatedOrder, 'status' | 'delivery_info' | 'seller_id' | 'pvz'>, IOrderDeliveryMeta, IOrderTimeline, IOrderSuperOrder, IOrderCancelPenalty, IOrderSellerNotified {
  status: EOrderStatus
  seller_id?: Components.Schemas.IPopulatedOrderSeller & IOrderSellerDeadlines & IOrderSellerCancelCount & IOrderSellerDeliveryPoints
  pvz?: IOrderPickupPoint | null
}
// variation_characteristic бэкенд отдаёт только в карточке заказа, в схеме api-пакета его нет
export interface IOrderItem extends Omit<Components.Schemas.IPopulatedOrderItem, 'product_id'> {
  product_id: Components.Schemas.IPopulatedOrderItem['product_id'] & {
    variation_characteristic?: Components.Schemas.ICharacteristic[]
  }
}
export interface IAdminChangeOrderStatus extends Omit<Components.Schemas.IAdminChangeOrderStatus, 'status'> {
  status: EOrderStatus
}
export interface IAdminGetOrderResponse
  extends Omit<Components.Schemas.IAdminGetOrderResponse, 'order'> {  //У Дани нет доступа к апдейту пакета, потом убрать
  order: ITableOrder
  seller_penalty?: ISellerCancelPenalty | null
  // Платформа супер-заказа, в котором оформлен заказ; null — заказ до появления поля
  platform?: TClientPlatform | null
}
export interface IAdminChangeOrderStatusResponse extends IOrderTimeline {
  seller_penalty?: ISellerCancelPenalty | null
}
// count бэкенд принимает, но в схеме api-пакета его пока нет
export interface IAdminDenyItem extends Components.Schemas.IAdminDenyItem {
  count?: number
}
export interface IAdminUpdateOrderRequest extends Components.Schemas.IAdminUpdateOrderRequest { }
// В супер-заказе промокод приходит развёрнутым, в остальных ответах это только id
export interface ISuperOrderPromoCode {
  _id: string
  code: string
  discount_type: EDiscountType
  discount_value: number
}
export interface ISuperOrderItem extends ITableOrder {
  promo_code?: ISuperOrderPromoCode | null
}
export enum EOrderPaymentMethod {
  CARD = 'CARD',
  INVOICE = 'INVOICE'
}

// Ссылка ЮKassa на оплату супер-заказа. url = null — действующей ссылки нет: платёж
// не создан или ЮKassa его закрыла; expires_at — до какого момента ссылка работает
export interface IAdminPaymentLink {
  url: string | null
  expires_at: string | null
  // Товары, которых не хватает для оплаты: пока список не пуст, новую ссылку не создать.
  // Сервер до выпуска поля его не присылает
  sold_out?: string[]
}
// link = null — заказ не ждёт оплаты картой
export interface IAdminPaymentLinkResponse {
  link: IAdminPaymentLink | null
}

export interface IInvoicePayer {
  inn: string
  kpp: string | null
  name: string
}

export interface IAdminMultiOrderResponse extends Omit<Components.Schemas.IAdminMultiOrderResponse, 'orders'> {
  orders: ISuperOrderItem[]
  // Старые супер-заказы без поля оплачены картой
  payment_method?: EOrderPaymentMethod
  // null — супер-заказ до появления поля
  platform?: TClientPlatform | null
  invoice_payer?: IInvoicePayer | null
}

export enum EMultiOrderStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  CANCELED = 'CANCELED'
}

// Строка списка супер-заказов — заказы внутри не разворачиваются, приходят их id
export interface IMultiOrderRow {
  _id: string
  number: number
  sum: number
  status: EMultiOrderStatus
  orders: string[]
  createdAt: string
  user_id?: Components.Schemas.IPopulatedOrder['user_id']
  payment_method?: EOrderPaymentMethod
}
export interface IMultiOrdersQueryRequest {
  skip: string
  limit: string
  name?: string
  from?: string
  to?: string
  number?: string
  tel?: string
  sum?: 'asc' | 'desc'
  user_id?: string
}
export interface IMultiOrdersQueryResponse {
  multiOrders: IMultiOrderRow[]
  count: number
}

export enum EOrderStatus {
  NOT_PAID = 'NOT_PAID',
  ON_CONFIRMATION = 'ON_CONFIRMATION',
  CONFIRMED_BY_SELLER = 'CONFIRMED_BY_SELLER',
  IN_DELIVERY = 'IN_DELIVERY',
  WAITING_IN_POINT = 'WAITING_IN_POINT',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  ARCHIVED = 'ARCHIVED',
  RETURNED_TO_SENDER = 'RETURNED_TO_SENDER',
  AWAITING_RETURN = 'AWAITING_RETURN',
  RETURNED = 'RETURNED',
}

export enum ECdekBarcodeFormat {
  A4 = 'A4',
  A5 = 'A5',
  A6 = 'A6',
  A7 = 'A7',
}

export enum ERecieveMethod {
  PICKUP_POINT = 'PICKUP_POINT',
  EXPRESS = 'EXPRESS',
  COURIER = 'COURIER',
  PVZ = 'PVZ',
  // Везёт сам продавец, без перевозчика
  OWN_DELIVERY = 'OWN_DELIVERY',
}

export enum EOrderItemsStatus {
  AGREED = 'AGREED',
  DENIED = 'DENIED',
}

export interface IUpdateAdminOrderFormData {
  deliveryPrice?: string
  trackNumber?: string
  invoice: any
  deliveryType: ERecieveMethod
  prepareDate: any
  shopId?: string
  deliveryAddress?: string
  note: string
}
